<script lang="ts">
  import { isEditActive } from "$src/application/main/mainTemplateStore";
  import {
    projectDraft,
    ProjectFormSection,
    projectOriginal,
    registerErrors,
  } from "$src/application/projects/stores/projectStore";
  import { getCustomerInfo } from "$src/domain/customers/utils/customer-info-utils";
  import { getTranslation } from "$src/presentation/core/TranslatedTextUtils";
  import { Mail, MapPin, Pencil, Phone } from "lucide-svelte";
  import { languageCode } from "$src/presentation/core/languageCode";
  import AddressValue from "$src/presentation/core/address-value.svelte";
  import { Label } from "$lib/components/ui/label";
  import SelectCustomerType from "./select-customer-type.svelte";
  import { t } from "svelte-i18n";
  import { createForm } from "felte";
  import {
    type Customer,
    customerSchema,
  } from "$src/domain/customers/Customer";
  import * as zod from "zod";
  import { validator } from "@felte/validator-zod";
  import ZodTranslatedMessage from "$src/presentation/core/ZodTranslatedMessage.svelte";
  import { Input } from "$lib/components/ui/input";
  import { cn } from "$lib/utils";
  import Button from "$lib/components/ui/button/button.svelte";
  import * as Alert from "$lib/components/ui/alert/index.js";
  import { TriangleAlert } from "lucide-svelte";
  import { onDestroy } from "svelte";
  import * as Tooltip from "$lib/components/ui/tooltip/index.js";
  import AssignCustomerDialog from "./assign-customer-dialog.svelte";
  import {
    toCustomer,
    type CustomerSummary,
  } from "$src/domain/customers/CustomerSummary";
  import { type CustomerType } from "$src/domain/customers/CustomerType";
  import { get } from "svelte/store";
  import { type Geocode } from "$src/domain/map/GeoCode";
  import CustomerContactsTable from "./customer-contacts-table.svelte";

  export let patchHandler: () => void;

  export let groupStoreId: number;

  let isEditingCustomer: boolean = false;

  type FormValues = zod.infer<typeof customerSchema>;

  $: editForm = createForm({
    onSubmit: handleSubmit,
    extend: validator({ schema: customerSchema }),
  });

  $: form = editForm.form;
  $: data = editForm.data;
  $: errors = editForm.errors;

  function handleSubmit() {}

  const editUnsubscribe = isEditActive.subscribe((isEditing) => {
    if (!isEditing) {
      isEditingCustomer = false;
      var draft = get(projectDraft);
      if (editForm && draft) {
        editForm.setInitialValues({
          ...draft.customer,
        });
      }
    }
  });

  onDestroy(() => {
    if (editUnsubscribe) editUnsubscribe();
  });

  let originalCustomer: Customer;
  let assignedCustomer: Customer | undefined = undefined;

  function startEditCustomer() {
    isEditingCustomer = true;
    originalCustomer = JSON.parse(JSON.stringify($projectOriginal.customer));

    var draft = get(projectDraft);
    if (editForm && draft) {
      editForm.setInitialValues({
        ...draft.customer,
      });
      setTimeout(() => editForm.validate(), 500);
    }
  }

  function cancelEditingCustomer() {
    isEditingCustomer = false;
    $projectDraft.customer = assignedCustomer ?? originalCustomer;
    patchHandler();
  }

  function assignCustomer(e: CustomEvent<CustomerSummary>) {
    assignedCustomer = toCustomer(e.detail);
    $projectDraft.customer = JSON.parse(JSON.stringify(assignedCustomer));
    patchHandler();
  }

  $: {
    registerErrors(ProjectFormSection.InfoCustomer, $errors);
  }

  function assignCutomerType(selectedType: CustomEvent<CustomerType>) {
    $data.customerType = selectedType.detail;

    setTimeout(() => editForm.validate(), 500);
  }

  function onGeoCoded(e: CustomEvent<Geocode>) {
    console.log("customer : ", $projectDraft.customer);
    console.log("location : ", e.detail.location);

    $projectDraft.customer.billingAddressLocation = e.detail.location;
    patchHandler();
  }
</script>

<div
  class="flex-1 grid w-full grid-cols-2 gap-2 p-2 md:max-w-xs min-w-52 items-start"
>
  <div class="col-span-full flex gap-0 items-center flex-wrap overflow-hidden">
    <!-- Edit mode -->
    {#if $isEditActive && $projectDraft && isEditingCustomer}
      <form use:form>
        <div class="border flex flex-col gap-2 flex-auto p-2 rounded-lg">
          <div class="grid w-full h-full grid-cols-2 gap-2 max-w-md p-2">
            <!-- Customer Type -->
            <div class="col-span-full flex gap-2 items-center flex-wrap">
              <Label
                for="customerType"
                class="text-right text-xs text-muted-foreground"
              >
                {$t("page.customers.labels.type")}
              </Label>
              <SelectCustomerType
                {groupStoreId}
                bind:value={$projectDraft.customer.customerType}
                on:selectType={patchHandler}
                on:selectType={assignCutomerType}
              />
            </div>
            <!-- Lastname / Firstname / Company name / VAT -->
            {#if $projectDraft.customer.customerType && $projectDraft.customer.customerType.id == 1}
              <div class="col-span-full flex items-center flex-wrap">
                <Label class="text-xs text-muted-foreground">
                  {$t("page.customers.labels.lastname")}
                </Label>
                <Input
                  name="lastname"
                  bind:value={$projectDraft.customer.lastname}
                  class={cn(
                    "col-span-3",
                    $errors.lastname ? "border-red-500" : ""
                  )}
                  on:input={patchHandler}
                />
                <!-- on:input={debouncedValidateForm} -->
                {#if $errors.lastname}
                  <div class="text-xs text-red-500 w-full text-right">
                    <ZodTranslatedMessage messages={$errors.lastname} />
                  </div>
                {/if}
              </div>
              <div class="col-span-full flex items-center flex-wrap">
                <Label for="firstname" class="text-xs text-muted-foreground">
                  {$t("page.customers.labels.firstname")}
                </Label>
                <Input
                  name="firstname"
                  bind:value={$projectDraft.customer.firstname}
                  on:input={patchHandler}
                  class={cn(
                    "col-span-3",
                    $errors.firstname ? "border-red-500" : ""
                  )}
                />

                <!-- on:input={debouncedValidateForm} -->
                {#if $errors.firstname}
                  <div class="text-xs text-red-500 w-full text-right">
                    <ZodTranslatedMessage messages={$errors.firstname} />
                  </div>
                {/if}
              </div>
            {:else if $projectDraft.customer.customerType && $projectDraft.customer.customerType.id > 1}
              <div class="col-span-full flex items-center flex-wrap">
                <Label for="companyName" class="text-xs text-muted-foreground">
                  {$t("page.customers.labels.companyName")}
                </Label>
                <Input
                  name="companyName"
                  bind:value={$projectDraft.customer.companyName}
                  class={cn(
                    "col-span-3",
                    $errors.companyName ? "border-red-500" : ""
                  )}
                />
                {#if $errors.companyName}
                  <div class="text-xs text-red-500 w-full text-right">
                    <ZodTranslatedMessage messages={$errors.companyName} />
                  </div>
                {/if}
              </div>
              <div class="col-span-full flex items-center flex-wrap">
                <Label for="vatNumber" class="text-xs text-muted-foreground">
                  {$t("page.customers.labels.vatNumber")}
                </Label>
                <Input
                  name="vatNumber"
                  bind:value={$projectDraft.customer.vatNumber}
                  class={cn(
                    "col-span-3",
                    $errors.vatNumber ? "border-red-500" : ""
                  )}
                />
                {#if $errors.vatNumber}
                  <div class="text-xs text-red-500 w-full text-right">
                    <ZodTranslatedMessage messages={$errors.vatNumber} />
                  </div>
                {/if}
              </div>
            {/if}
            <!-- Address -->
            <div class="col-span-full flex items-center flex-wrap">
              <AddressValue
                bind:value={$projectDraft.customer.billingAddress}
                {patchHandler}
                geoCodeActivated={true}
                on:geoCoded={onGeoCoded}
                editMode={true}
                displayGeoCode={true}
              />
            </div>
            <!-- Phone number -->
            <div class="col-span-full flex items-center flex-wrap">
              <Label for="phoneNumber1" class="text-xs text-muted-foreground">
                {$t("page.customers.labels.phoneNumber1")}
              </Label>
              <Input
                name="phoneNumber1"
                type="phone"
                bind:value={$projectDraft.customer.phoneNumber1}
                on:input={patchHandler}
                class={cn(
                  "col-span-3",
                  $errors.phoneNumber1 ? "border-red-500" : ""
                )}
              />
              <!-- on:input={debouncedValidateForm} -->
              {#if $errors.phoneNumber1}
                <div class="text-xs text-red-500 w-full text-right">
                  <ZodTranslatedMessage messages={$errors.phoneNumber1} />
                </div>
              {/if}
            </div>
            <!-- Email -->
            <div class="col-span-full flex items-center flex-wrap">
              <Label for="email" class="text-xs text-muted-foreground">
                {$t("page.customers.labels.email")}
              </Label>
              <Input
                name="email"
                type="email"
                bind:value={$projectDraft.customer.email}
                on:input={patchHandler}
                class={cn("col-span-3", $errors.email ? "border-red-500" : "")}
              />
              <!-- on:input={debouncedValidateForm} -->
              {#if $errors.email}
                <div class="text-xs text-red-500 w-full text-right">
                  <ZodTranslatedMessage messages={$errors.email} />
                </div>
              {/if}
            </div>
          </div>

            <div class="flex-none">
              <CustomerContactsTable
                isEditMode={isEditingCustomer}
                {patchHandler}
              />
            </div>

          <div class="flex justify-end">
            <Button class="text-xs" on:click={cancelEditingCustomer}>
              {$t("global.messages.cancel")}
            </Button>
          </div>
        </div>
      </form>
    {:else}
      <!-- View mode -->
      <div
        class="relative grid w-full h-full grid-cols-2 gap-2 pl-4 md:pl-8 p-4 text-sm border rounded-lg bg-muted/80"
      >
        <!-- User name -->
        <div class="col-span-full flex gap-2 items-center">
          <div class="ml-1">
            <div class="text-lg font-semibold">
              {getCustomerInfo(
                $isEditActive
                  ? $projectDraft.customer
                  : $projectOriginal.customer
              )}
            </div>
            <div class="text-sm font-semibold">
              {#if $isEditActive}
                {getTranslation(
                  $projectDraft.customer.customerType.translations,
                  $languageCode
                )}
              {:else}
                {getTranslation(
                  $projectOriginal.customer.customerType.translations,
                  $languageCode
                )}
              {/if}
            </div>
          </div>
        </div>
        <div class="col-span-full flex items-center flex-wrap">
          <div class="flex mb-2">
            <MapPin class="w-4 h-4 mr-4" />
            <AddressValue
              value={$isEditActive
                ? $projectDraft.customer.billingAddress
                : $projectOriginal.customer.billingAddress}
              googleLink={true}
            />
          </div>
        </div>
        <div class="col-span-full flex items-center flex-wrap">
          <div class="flex">
            <Mail class="w-4 h-4 mr-4 " />
            <a
              href="mailto:{$isEditActive
                ? $projectDraft.customer.email ?? ''
                : $projectOriginal.customer.email ?? ''}"
              class="text-sm hover:text-secondary-foreground"
            >
              {$isEditActive
                ? $projectDraft.customer.email ?? "/"
                : $projectOriginal.customer.email ?? "/"}
            </a>
          </div>
        </div>
        <div class="col-span-full flex items-center flex-wrap">
          <div class="flex">
            <Phone class="w-4 h-4 mr-4" />
            <p class="text-sm">
              {$isEditActive
                ? $projectDraft.customer.phoneNumber1
                : $projectOriginal.customer.phoneNumber1}
            </p>
          </div>
        </div>

        {#if $projectOriginal.customer.contacts && $projectOriginal.customer.contacts.length > 0}
          <div class="col-span-full pt-2">
            <CustomerContactsTable  {patchHandler} />
          </div>
        {/if}

        {#if $isEditActive && $projectDraft}
          <div class="col-span-full flex items-center flex-wrap">
            <Alert.Root variant="warning" class="col-span-full p-2">
              <TriangleAlert class="h-4 w-4" />
              <Alert.Title class="ml-2">
                {$t("global.messages.warning")}
              </Alert.Title>
              <Alert.Description class="text-xs ml-2">
                {$t("page.projects.detail.customer-warning")}
              </Alert.Description>
            </Alert.Root>

            {#if $projectDraft.customer.id != $projectOriginal.customer.id}
              <Alert.Root variant="warning" class="col-span-full p-2">
                <TriangleAlert class="h-4 w-4" />
                <Alert.Title class="ml-2">
                  {$t("page.projects.detail.customer-transfer-warning")}
                </Alert.Title>
                <Alert.Description class="text-xs ml-2">
                  {$t("page.projects.detail.customer-transfer-info")} :
                  {getCustomerInfo($projectOriginal.customer)}.
                  {$t("page.projects.detail.customer-transfer-info2")} :
                </Alert.Description>
              </Alert.Root>
            {/if}
          </div>

          <div class="absolute top-2 right-2 flex flex-col gap-2">
            <Tooltip.Root>
              <Tooltip.Trigger asChild let:builder>
                <Button
                  builders={[builder]}
                  size="miniAction"
                  class="rounded-full  transition-transform transform  hover:scale-110"
                  on:click={startEditCustomer}
                >
                  <Pencil size="20" />
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Content>
                <p>{$t("page.projects.detail.edit-customer")}</p>
              </Tooltip.Content>
            </Tooltip.Root>

            <AssignCustomerDialog
              {groupStoreId}
              selectedStore={$projectDraft.info.store}
              on:selectCustomer={assignCustomer}
            />
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>
